import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ApiResponse } from '../tiposGlobais'

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5160/api',
      timeout: 120000, // 2 minutos
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        
        // Se o data for FormData, remover Content-Type padrão para que o navegador defina automaticamente com boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response
      },
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          // Só redireciona se já houver um token (sessão expirada)
          // Não redireciona durante login inicial (sem token ainda)
          const token = localStorage.getItem('token');
          if (token && !window.location.pathname.includes('/login')) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
          }
          // Se não houver token, é um erro de login e não deve redirecionar
        }
        
        // Capturar mensagens de erro do backend
        if (error.response?.data) {
          const errorData = error.response.data
          if (typeof errorData === 'string') {
            error.message = errorData
          } else if (errorData.message) {
            error.message = errorData.message
          } else if (errorData.error) {
            error.message = errorData.error
          }
        }
        
        return Promise.reject(error)
      }
    )
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config)
    // Se responseType for 'blob', retornar data diretamente
    if (config?.responseType === 'blob') {
      return response.data as T
    }
    return response.data
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config)
    return response.data
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config)
    return response.data
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(url, data, config)
    return response.data
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config)
    return response.data
  }
}

export const apiService = new ApiService()
export const api = new ApiService()
export default apiService